import { useState, useEffect } from 'react';
import { Escort } from '../types';
import { fetchEscorts, searchEscorts, getEscortById, SearchCriteria } from '../data/escorts';
import { useCountryContext } from '../contexts/CountryContext';
import { logApiAttempt } from '../utils/log';

interface UseEscortsReturn {
  escorts: Escort[];
  loading: boolean;
  error: string | null;
  apiConnected: boolean;
  refetch: () => Promise<void>;
  searchByLocation: (location: string) => Promise<void>;
  searchByFilter: (criteria: SearchCriteria) => Promise<Escort[]>;
  getById: (id: string) => Promise<Escort | null>;
  getFeaturedEscorts: () => Escort[];
  getVetrinaEscorts: () => Escort[];
  getRegularEscorts: () => Escort[];
}

export const useEscortsData = (): UseEscortsReturn => {
  const [escorts, setEscorts] = useState<Escort[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiConnected, setApiConnected] = useState(false);
  const { getCountryModel, selectedCountry } = useCountryContext();

  const loadEscorts = async () => {
    try {
      setLoading(true);
      setError(null);

      logApiAttempt('🔄 Tentativo di caricamento escort dall\'API...');
      logApiAttempt('📍 URL API:' + import.meta.env.VITE_API_URL || 'non configurato');
      logApiAttempt('🔑 Token API:' + import.meta.env.VITE_API_TOKEN ? 'configurato' : 'non configurato');

      const countryModel = getCountryModel();
      const data = await fetchEscorts(countryModel);

      logApiAttempt('✅ Dati ricevuti dall\'API:' + data.length + 'escort');
      logApiAttempt('📊 Primo escort:' + data[0]?.name || 'nessun dato');

      setEscorts(data);
      setApiConnected(true);
      logApiAttempt('✅ Escort impostati nel state');
      logApiAttempt('🔌 API connessa:' + true);
    } catch (err) {
      logApiAttempt('❌ Errore nel caricamento escort:' + err);
      setError('Errore nel caricamento dei dati dall\'API');
      setEscorts([]);
      setApiConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const searchByLocation = async (location: string) => {
    try {
      setLoading(true);
      setError(null);
      const countryModel = getCountryModel();
      const data = await searchEscorts({ location }, countryModel);
      setEscorts(data);
      setApiConnected(true);
    } catch (err) {
      logApiAttempt('Search failed:' + err);
      setError('Errore nella ricerca per location');
      setEscorts([]);
      setApiConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const searchByFilter = async (criteria: SearchCriteria): Promise<Escort[]> => {
    try {
      setLoading(true);
      setError(null);
      const countryModel = getCountryModel();
      const data = await searchEscorts(criteria, countryModel);
      setEscorts(data);
      setApiConnected(true);
      return data;
    } catch (err) {
      logApiAttempt('Filter search failed:' + err);
      setError('Errore nel filtro di ricerca');
      setEscorts([]);
      setApiConnected(false);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getById = async (id: string): Promise<Escort | null> => {
    try {
      const countryModel = getCountryModel();
      return await getEscortById(id, countryModel);
    } catch (err) {
      logApiAttempt('Failed to get escort by ID:' + err);
      return null;
    }
  };

  const getFeaturedEscorts = (): Escort[] => {
    return escorts.filter(escort => escort.featured);
  };

  const getVetrinaEscorts = (): Escort[] => {
    return escorts.filter(escort => escort.isVetrina);
  };

  const getRegularEscorts = (): Escort[] => {
    return escorts.filter(escort => !escort.isVetrina);
  };


  useEffect(() => {
    // Carica i dati solo se necessario
    if (escorts.length === 0) {
      loadEscorts();
    }
  }, [escorts.length]);

  // Ricarica quando cambia il paese selezionato
  useEffect(() => {
    loadEscorts();
  }, [selectedCountry]);

  return {
    escorts,
    loading,
    error,
    apiConnected,
    refetch: loadEscorts,
    searchByLocation,
    searchByFilter,
    getById,
    getFeaturedEscorts,
    getVetrinaEscorts,
    getRegularEscorts,
  };
};

export default useEscortsData;
